import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import useUISettings from './hooks/useUISettings';

// Layout
import Header from './components/Common/Header';
import Footer from './components/Common/Footer';

// Pages
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import TextToSpeechDashboard from './pages/TextToSpeechDashboard';
import AudioToolDashboard from './pages/AudioToolDashboard';
import VoiceCloning from './pages/VoiceCloning';
import Profile from './pages/Profile';
import Billing from './pages/Billing';
import Admin from './pages/Admin';
import NotFound from './pages/NotFound';

// Auth Pages
import Login from './pages/Login';
import Signup from './pages/Signup';

function upsertMetaTag(attribute, key, value) {
  let tag = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!value) {
    if (tag) tag.remove();
    return;
  }

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', value);
}

function upsertFavicon(href) {
  if (!href) return;

  let icon = document.querySelector("link[rel='icon']");
  if (!icon) {
    icon = document.createElement('link');
    icon.setAttribute('rel', 'icon');
    document.head.appendChild(icon);
  }
  icon.setAttribute('href', href);
}

// Protected Route wrapper
function ProtectedRoute({ children, adminOnly = false }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function App() {
  const uiSettings = useUISettings();

  useEffect(() => {
    document.title = uiSettings.seoTitle || uiSettings.siteName;
    upsertMetaTag('name', 'description', uiSettings.seoDescription || uiSettings.siteDescription);
    upsertMetaTag('name', 'keywords', uiSettings.seoKeywords);
    upsertMetaTag('property', 'og:image', uiSettings.seoOgImage);
    upsertFavicon(uiSettings.faviconUrl);
  }, [
    uiSettings.siteName,
    uiSettings.siteDescription,
    uiSettings.seoTitle,
    uiSettings.seoDescription,
    uiSettings.seoKeywords,
    uiSettings.seoOgImage,
    uiSettings.faviconUrl,
  ]);

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      <Header />
      
      <main className="flex-grow">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/text-to-speech"
            element={
              <ProtectedRoute>
                <TextToSpeechDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/voice-cleaner"
            element={
              <ProtectedRoute>
                <TextToSpeechDashboard
                  title="Voice Cleaner"
                  description="Generate or paste text, then produce polished narration with Voice Cleaner enabled by default."
                  defaultVoiceCleanerEnabled
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="/realtime-speech-to-text"
            element={
              <ProtectedRoute>
                <AudioToolDashboard toolId="realtime-speech-to-text" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/speech-to-text"
            element={
              <ProtectedRoute>
                <AudioToolDashboard toolId="speech-to-text" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/voice-changer"
            element={
              <ProtectedRoute>
                <AudioToolDashboard toolId="voice-changer" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/text-to-sound-effects"
            element={
              <ProtectedRoute>
                <AudioToolDashboard toolId="text-to-sound-effects" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/voice-isolator"
            element={
              <ProtectedRoute>
                <AudioToolDashboard toolId="voice-isolator" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/voice-cloning"
            element={
              <ProtectedRoute>
                <VoiceCloning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <Billing />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute adminOnly>
                <Admin />
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
}

export default App;
